import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  updatePerformerProfile,
  updateUserLocation,
  addProfession,
  scanResume,
  addProfileCredit,
  addSkill,
  uploadFile,
  updateProfile,
} from '../services/api';
import './Auth.css';

const professionOptions = [
  'Actor',
  'Singer',
  'Dancer',
  'Stage Manager',
  'Director',
  'Choreographer',
  'Music Director',
  'Playwright',
  'Composer',
  'Casting Director',
  'Producer',
  'Lighting Designer',
  'Sound Designer',
  'Set Designer',
  'Costume Designer',
  'Dramaturg',
  'Stage Hand',
  'Fight Choreographer',
  'Intimacy Coordinator',
  'Voice Coach',
];

function Register() {
  const navigate = useNavigate();
  const { register, user, completeOnboarding } = useContext(AuthContext);

  // Step tracking
  const [step, setStep] = useState(1);

  // Step 1: Account
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 2: Profile
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [customProfession, setCustomProfession] = useState('');
  const [tagline, setTagline] = useState('');
  const [headline, setHeadline] = useState('');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const photoInputRef = useRef(null);
  const resumeInputRef = useRef(null);

  // SHO-40: if the user logs out (or otherwise loses their session) after
  // creating their account, kick them back to /login instead of leaving them
  // on a stale setup form that would crash on user-dependent fields and
  // require an extra "Skip" tap to escape.
  useEffect(() => {
    if (!user && step > 1) {
      navigate('/login');
    }
  }, [user, step, navigate]);

  // Step 3: Resume
  const [resumeType, setResumeType] = useState('performer');
  const [resumeDragging, setResumeDragging] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scannedCredits, setScannedCredits] = useState(null);
  const [scannedEducation, setScannedEducation] = useState([]);
  const [scannedSkills, setScannedSkills] = useState([]);
  const [scanError, setScanError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await register(formData);
    if (!result.success) {
      setError(result.error);
      setLoading(false);
    } else {
      setStep(2);
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProfilePhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const toggleProfession = (prof) => {
    setSelectedProfessions((prev) =>
      prev.includes(prof) ? prev.filter((p) => p !== prof) : [...prev, prof]
    );
  };

  const addCustom = () => {
    if (customProfession.trim() && !selectedProfessions.includes(customProfession.trim())) {
      setSelectedProfessions((prev) => [...prev, customProfession.trim()]);
      setCustomProfession('');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const promises = [];

      if (profilePhoto && user) {
        const uploadRes = await uploadFile(profilePhoto);
        const imageUrl = uploadRes.data?.url;
        if (imageUrl) {
          promises.push(
            updateProfile(user.id, {
              full_name: user.full_name,
              profile_image: imageUrl,
              profile_image_x: 50,
              profile_image_y: 50,
              profile_image_zoom: 100,
            })
          );
        }
      }

      if (tagline) promises.push(updatePerformerProfile({ tagline }));
      if (headline && user)
        promises.push(updateProfile(user.id, { full_name: user.full_name, headline }));
      if (location) promises.push(updateUserLocation(location));
      for (const prof of selectedProfessions) {
        promises.push(addProfession({ profession: prof }));
      }

      await Promise.all(promises);
    } catch (err) {
      console.error('Error saving profile:', err);
    }
    setSaving(false);
    setStep(3);
  };

  const handleResumeDrop = (e) => {
    e.preventDefault();
    setResumeDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleResumeFile(file);
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) handleResumeFile(file);
  };

  const handleResumeFile = async (file) => {
    if (!file) return;
    setResumeFile(file);
    setScanning(true);
    setScanError('');
    try {
      const res = await scanResume(file, resumeType);
      const credits = res.data.credits || [];
      const education = res.data.education || [];
      const skills = res.data.skills || [];
      setScannedCredits(credits);
      setScannedEducation(education);
      setScannedSkills(skills);
      if (credits.length === 0 && education.length === 0 && skills.length === 0) {
        setScanError('Nothing found. Try a clearer image or PDF.');
      }
    } catch (err) {
      console.error('Resume scan error:', err);
      setScanError(
        err.response?.data?.error || 'Could not scan resume. You can add credits manually later.'
      );
    }
    setScanning(false);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      if (scannedCredits?.length > 0) {
        for (const credit of scannedCredits) {
          await addProfileCredit({
            category: credit.category || 'Theatre',
            role: credit.role || '',
            production: credit.production || '',
            company: credit.company || '',
            director: credit.director || '',
            year: credit.year || '',
          });
        }
      }
      if (scannedSkills?.length > 0) {
        for (const skill of scannedSkills) {
          try {
            await addSkill({ category: skill.category || 'General', skill: skill.skill });
          } catch (e) {
            /* skip duplicates */
          }
        }
      }
    } catch (err) {
      console.error('Error saving resume data:', err);
    }
    const result = await completeOnboarding();
    if (!result.success) {
      console.error('Error completing onboarding:', result.error);
    }
    setSaving(false);
    navigate('/feed');
  };

  const handleSkipToFeed = async () => {
    const result = await completeOnboarding();
    if (!result.success) {
      console.error('Error completing onboarding:', result.error);
    }
    navigate('/feed');
  };

  // Step 1: Create Account
  if (step === 1) {
    return (
      <div className="auth-page" style={{ backgroundColor: '#5a407d' }}>
        <div className="auth-hero">
          <div className="auth-brand visible">Showcase</div>
          <div className="auth-phrase final">Your Network. Your Spotlight.</div>
        </div>

        <div
          className="auth-form-container visible"
          style={{ position: 'relative', bottom: 'auto', marginTop: '40px' }}
        >
          <div className="auth-form-card">
            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleCreateAccount} className="auth-form">
              <div className="auth-field">
                <input
                  type="text"
                  name="full_name"
                  className="auth-input"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Full Name"
                  aria-label="Full Name"
                />
              </div>
              <div className="auth-field">
                <input
                  type="text"
                  name="username"
                  className="auth-input"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Username"
                  aria-label="Username"
                  required
                />
              </div>
              <div className="auth-field">
                <input
                  type="email"
                  name="email"
                  className="auth-input"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  aria-label="Email"
                  required
                />
              </div>
              <div className="auth-field">
                <input
                  type="password"
                  name="password"
                  className="auth-input"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  aria-label="Create a password"
                  required
                />
              </div>
              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? 'Creating Account...' : 'Join Showcase'}
              </button>
            </form>

            <div className="auth-footer">
              Already on Showcase? <Link to="/login">Sign in</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Profile Setup
  if (step === 2) {
    return (
      <div className="auth-page" style={{ backgroundColor: '#2e2e2e' }}>
        <div className="register-setup">
          <div className="register-setup-header">
            <div className="register-setup-brand">Showcase</div>
            <div className="register-setup-steps">
              <div className="register-step-dot active" />
              <div className="register-step-line" />
              <div className="register-step-dot" />
            </div>
            <button type="button" className="register-skip" onClick={handleSkipToFeed}>
              Skip
            </button>
          </div>

          <div className="register-setup-card">
            <h1 className="register-setup-title">Set up your profile.</h1>
            <p className="register-setup-subtitle">
              This helps people find you and powers your connection suggestions.
            </p>

            {/* Profile Photo */}
            <div className="register-photo-section">
              <button
                type="button"
                className="register-photo-circle"
                onClick={() => photoInputRef.current?.click()}
                aria-label="Add profile photo"
              >
                {profilePhotoPreview ? (
                  <img src={profilePhotoPreview} alt="Profile" />
                ) : (
                  <div className="register-photo-placeholder">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span>Add Photo</span>
                  </div>
                )}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handlePhotoSelect}
                aria-label="Profile photo"
              />
            </div>

            {/* Professions */}
            <div className="register-field">
              <label>What do you do?</label>
              <div className="register-professions">
                {professionOptions.map((prof) => (
                  <button
                    type="button"
                    key={prof}
                    className={`register-profession-chip ${selectedProfessions.includes(prof) ? 'selected' : ''}`}
                    onClick={() => toggleProfession(prof)}
                  >
                    {prof}
                  </button>
                ))}
              </div>
              <div className="register-custom-row">
                <input
                  type="text"
                  aria-label="Other profession"
                  placeholder="Other..."
                  value={customProfession}
                  onChange={(e) => setCustomProfession(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustom())}
                />
                {customProfession.trim() && (
                  <button type="button" onClick={addCustom}>
                    Add
                  </button>
                )}
              </div>
            </div>

            {/* Tagline */}
            <div className="register-field">
              <label htmlFor="register-tagline">Your tagline</label>
              <input
                id="register-tagline"
                type="text"
                className="register-input"
                placeholder="e.g. Broadway actress & singer"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
              />
            </div>

            {/* Headline */}
            <div className="register-field">
              <label htmlFor="register-headline">Your networking headline</label>
              <input
                id="register-headline"
                type="text"
                className="register-input"
                placeholder="e.g. Hamilton on Broadway | BFA NYU Tisch | AEA"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
              />
              <span className="register-field-hint">
                This shows on your card when people find you. Include your best credits, school, or
                union status.
              </span>
            </div>

            {/* Location */}
            <div className="register-field">
              <label htmlFor="register-location">Where are you based?</label>
              <input
                id="register-location"
                type="text"
                className="register-input"
                placeholder="e.g. New York, NY"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="register-continue-btn"
              onClick={handleSaveProfile}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Resume Upload
  return (
    <div className="auth-page" style={{ backgroundColor: '#2e2e2e' }}>
      <div className="register-setup">
        <div className="register-setup-header">
          <div className="register-setup-brand">Showcase</div>
          <div className="register-setup-steps">
            <div className="register-step-dot active" />
            <div className="register-step-line" />
            <div className="register-step-dot active" />
          </div>
          <button type="button" className="register-skip" onClick={handleSkipToFeed}>
            Skip
          </button>
        </div>

        <div className="register-setup-card">
          <h1 className="register-setup-title">Upload your resume.</h1>
          <p className="register-setup-subtitle">
            We'll scan it to find your credits and connect you with people you've worked with.
          </p>

          <div className="register-field">
            <label>What type of resume?</label>
            <div className="register-resume-types">
              {[
                { id: 'performer', label: 'Performer' },
                { id: 'director', label: 'Director' },
                { id: 'stage_manager', label: 'Stage Manager' },
                { id: 'choreographer', label: 'Choreographer' },
                { id: 'designer', label: 'Designer' },
                { id: 'general', label: 'Other' },
              ].map((t) => (
                <button
                  key={t.id}
                  className={`register-resume-type-btn ${resumeType === t.id ? 'selected' : ''}`}
                  type="button"
                  onClick={() => setResumeType(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="register-resume-area">
            {!resumeFile ? (
              <label
                className={`register-resume-dropzone ${resumeDragging ? 'dragging' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setResumeDragging(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setResumeDragging(true);
                }}
                onDragLeave={() => setResumeDragging(false)}
                onDrop={handleResumeDrop}
              >
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <polyline points="9 15 12 12 15 15" />
                </svg>
                <span>
                  {resumeDragging ? 'Drop it here!' : 'Drop your resume here or click to upload'}
                </span>
                <span className="register-resume-formats">PDF, PNG, or JPEG</span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  style={{ display: 'none' }}
                  onChange={handleResumeUpload}
                  aria-label="Resume file"
                />
              </label>
            ) : scanning ? (
              <div className="register-scanning">
                <div className="register-spinner" />
                <p>Scanning your resume...</p>
                <p className="register-scan-hint">This usually takes 10-15 seconds</p>
              </div>
            ) : scanError ? (
              <div className="register-scan-result">
                <div className="register-scan-fail">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#f87171"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  <p className="register-scan-error">{scanError}</p>
                </div>
                <label className="register-retry-dropzone">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  <span>Try a different file</span>
                  <input
                    ref={resumeInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      setScanError('');
                      setResumeFile(null);
                      setScannedCredits(null);
                      handleResumeUpload(e);
                    }}
                    aria-label="Retry resume file"
                  />
                </label>
              </div>
            ) : scannedCredits ? (
              <div className="register-scan-result">
                <div className="register-scan-success">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4ade80"
                    strokeWidth="2"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>
                    Found {scannedCredits.length} credit{scannedCredits.length !== 1 ? 's' : ''}
                    {scannedSkills.length > 0 &&
                      `, ${scannedSkills.length} skill${scannedSkills.length !== 1 ? 's' : ''}`}
                    {scannedEducation.length > 0 && `, ${scannedEducation.length} education`}
                  </span>
                </div>
                <div className="register-credits-list">
                  {scannedCredits.slice(0, 5).map((credit, i) => (
                    <div key={i} className="register-credit-row">
                      <strong>{credit.role}</strong>
                      <span>
                        {credit.production}
                        {credit.company ? ` — ${credit.company}` : ''}
                      </span>
                    </div>
                  ))}
                  {scannedCredits.length > 5 && (
                    <p className="register-credits-more">
                      +{scannedCredits.length - 5} more credits
                    </p>
                  )}
                  {scannedSkills.length > 0 && (
                    <div className="register-skills-preview">
                      {scannedSkills.slice(0, 8).map((s, i) => (
                        <span key={i} className="register-skill-chip">
                          {s.skill}
                        </span>
                      ))}
                      {scannedSkills.length > 8 && (
                        <span className="register-credits-more">
                          +{scannedSkills.length - 8} more
                        </span>
                      )}
                    </div>
                  )}
                  {scannedEducation.length > 0 && (
                    <div className="register-education-preview">
                      {scannedEducation.map((ed, i) => (
                        <div key={i} className="register-credit-row">
                          <strong>{ed.degree || ed.program || 'Education'}</strong>
                          <span>
                            {ed.institution}
                            {ed.year ? ` — ${ed.year}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className="register-continue-btn"
            onClick={handleFinish}
            disabled={saving}
          >
            {saving
              ? 'Saving...'
              : scannedCredits?.length
                ? 'Save Credits & Get Started'
                : 'Get Started'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Register;
